/*
  Pure Firebase Cloud Messaging token helpers.

  DEPENDENCY RULE: this module may only import src/utils/firebase, firebase/messaging and
  src/config-global. Its whole transitive closure is src/config-global -> src/routes/paths ->
  src/utils/change-case + src/_mock/assets, none of which reach src/utils/axios. That is what lets
  src/utils/token.js import it statically: every src/api/* module pulls in src/utils/axios, which
  imports src/utils/token, so anything that talks to our own backend cannot live here -- it belongs
  in src/utils/messaging.js. import/no-cycle is disabled in .eslintrc, so lint will not catch a
  regression; keep the import list below short by hand.

  Every export resolves to a falsy value instead of throwing: the session-teardown callers are
  synchronous and can neither await nor catch.
*/
import { getToken, deleteToken } from "firebase/messaging";

// utils
import { messaging } from "src/utils/firebase";
// config
import { FIREBASE_API } from "src/config-global";

// messaging() resolves to `false` on browsers where FCM is unsupported (no ServiceWorker,
// PushManager or Notification API). getToken()/deleteToken() blow up on that value, so every
// caller funnels through here.
async function getSupportedMessaging() {
  try {
    return (await messaging()) || null;
  } catch (error) {
    return null;
  }
}

// The service worker FCM binds the push subscription to. We reuse the registration
// src/service-worker-registration.js already created for "/" rather than letting the SDK fall back
// to registering its default "/firebase-messaging-sw.js", a file this app does not ship (that
// fallback 404s into the SPA's index.html and throws). getRegistration() resolves to undefined when
// there is none -- unlike navigator.serviceWorker.ready, which simply never settles.
async function getPushServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) return undefined;

  try {
    return await navigator.serviceWorker.getRegistration();
  } catch (error) {
    return undefined;
  }
}

// Reads the token and, as a side effect, binds the SDK to our service worker registration so a
// later deleteToken() has one. Never prompts: getToken() opens the browser permission dialog when
// Notification.permission is still "default", which must not happen on a logout.
async function readToken(msg, registration) {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return null;

    const serviceWorkerRegistration = registration ?? (await getPushServiceWorkerRegistration());
    if (!serviceWorkerRegistration) return null;

    const fcmToken = await getToken(msg, {
      vapidKey: FIREBASE_API.vapidKey,
      serviceWorkerRegistration,
    });

    return fcmToken || null;
  } catch (error) {
    console.log(error);
    return null;
  }
}

/**
 * This device's FCM registration token, or null when it cannot be obtained (push unsupported,
 * permission not granted, no service worker, FCM unreachable). Never throws, never prompts.
 *
 * @param {ServiceWorkerRegistration} [registration] reused when the caller already has one.
 * @returns {Promise<string|null>}
 */
export async function getMessagingDeviceToken(registration) {
  const serviceWorkerRegistration = registration ?? (await getPushServiceWorkerRegistration());
  if (!serviceWorkerRegistration) return null;

  const msg = await getSupportedMessaging();
  if (!msg) return null;

  return readToken(msg, serviceWorkerRegistration);
}

/**
 * Revokes this device's FCM registration token: deletes it at FCM and unsubscribes the local push
 * subscription. Reading the token first is deliberate -- it primes messaging.swRegistration through
 * the public API, and an already valid token comes straight back out of IndexedDB with no network
 * call. Never throws.
 *
 * @param {ServiceWorkerRegistration} [registration]
 * @returns {Promise<boolean>} true when a delete was actually issued.
 */
export async function deleteMessagingDeviceToken(registration) {
  try {
    const serviceWorkerRegistration = registration ?? (await getPushServiceWorkerRegistration());
    if (!serviceWorkerRegistration) return null;

    const msg = await getSupportedMessaging();
    if (!msg) return false;

    await readToken(msg, serviceWorkerRegistration);
    await deleteToken(msg);

    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
}
